import { Button } from '../ui/button'
// @ts-strict-ignore
import { Icon } from '@blueprintjs/core'
import { Card } from '../ui/card'
import React, { useMemo, useState } from 'react'
import { EmptyState } from '../ui/empty-state'
import { PromotionItemsTableDummy } from './PromotionItemsTableDummy'
import { PromotionLinkedAdGroupsNew } from './PromotionLinkedAdGroupsNew'
import { PromotionRulesTable } from './PromotionRulesTable'
import { PromotionRulesNew } from './PromotionRulesNew'
import { PromotionItemAddDialog } from './PromotionItemAddDialog'
import { v4 as uuid } from 'uuid'
import PromotionItemI = Models.PromotionItemI
import { groupItems, NewPromotionValues } from '../../util/promotions'
import { SetItems } from '../../types/promotions'
import { useSetRecoilState } from 'recoil'
import { activeRuleAtom } from './promotion_state'
import PromotionRuleI = Models.PromotionRuleI

// ── New-editor empty states ───────────────────────────────────────────────────

const PlusIcon = () => (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" strokeWidth="1.8">
        <path d="M6 2.5v7M2.5 6h7" stroke="currentColor" strokeLinecap="round" />
    </svg>
)

const NewEmptyAdGroups = ({ onAdd, disabled }: { onAdd: () => void; disabled?: boolean }) => (
    <div className="flex flex-col items-center justify-center py-14 px-8 text-center">
        <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            className="text-[#DBE2E8] mb-4"
            strokeWidth="1.3"
        >
            <path
                d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
        <p className="text-[15px] font-[600] text-[#0E2947] mb-1">No ad groups linked</p>
        <p className="text-[14px] text-[#94A3B8] mb-5 max-w-[260px] leading-relaxed">
            Link the ad groups you want this campaign to manage
        </p>
        {!disabled && (
            <button
                type="button"
                onClick={onAdd}
                className="inline-flex items-center gap-1.5 px-4 h-8 text-[13px] font-[500] text-white bg-[#1A6FD4] hover:bg-[#1562BE] rounded-lg transition-colors"
            >
                <PlusIcon />
                Link Ad Group
            </button>
        )}
    </div>
)

const NewEmptyRules = ({ onAdd }: { onAdd: () => void }) => (
    <div className="flex flex-col items-center justify-center py-14 px-8 text-center">
        <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            className="text-[#DBE2E8] mb-4"
            strokeWidth="1.3"
        >
            <path
                d="M13 10h6M13 14h6M5 10h.01M5 14h.01"
                stroke="currentColor"
                strokeLinecap="round"
            />
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" />
            <path
                d="M8 10l-2 2 2 2"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
        <p className="text-[15px] font-[600] text-[#0E2947] mb-1">No rules yet</p>
        <p className="text-[14px] text-[#94A3B8] mb-5 max-w-[260px] leading-relaxed">
            Add a rule to start automating bid adjustments for this campaign
        </p>
        <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 px-4 h-8 text-[13px] font-[500] text-white bg-[#1A6FD4] hover:bg-[#1562BE] rounded-lg transition-colors"
        >
            <PlusIcon />
            Add rule
        </button>
    </div>
)

const PromotionDetailSkeleton = () => (
    <>
        <Card className="d-flex flex-column p-0 mb-4 bp4-card-rounded overflow-hidden">
            <div className="p-4 animate-pulse">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="h-4 w-36 rounded bg-[#E7ECF2] mb-2" />
                        <div className="h-3 w-56 rounded bg-[#EEF2F6]" />
                    </div>
                    <div className="h-8 w-28 rounded-lg bg-[#E7ECF2]" />
                </div>
                {[0, 1, 2, 3].map((row) => (
                    <div
                        key={row}
                        className="flex items-center gap-3 border-t border-[#EDF0F3] py-3"
                    >
                        <div className="size-2 rounded-full bg-[#D6DEE8]" />
                        <div className="h-4 flex-1 max-w-[360px] rounded bg-[#E7ECF2]" />
                        <div className="h-5 w-16 rounded-full bg-[#EEF2F6]" />
                        <div className="h-5 w-16 rounded-full bg-[#EEF2F6]" />
                        <div className="h-5 w-16 rounded-full bg-[#EEF2F6]" />
                    </div>
                ))}
            </div>
        </Card>
        <Card className="d-flex flex-column p-0 mb-4 bp4-card-rounded overflow-hidden">
            <div className="p-4 animate-pulse">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="h-4 w-24 rounded bg-[#E7ECF2] mb-2" />
                        <div className="h-3 w-48 rounded bg-[#EEF2F6]" />
                    </div>
                    <div className="h-8 w-20 rounded-lg bg-[#E7ECF2]" />
                </div>
                {[0, 1, 2].map((row) => (
                    <div key={row} className="border-t border-[#EDF0F3] py-3">
                        <div className="h-4 w-2/5 rounded bg-[#E7ECF2] mb-2" />
                        <div className="h-3 w-3/5 rounded bg-[#EEF2F6]" />
                    </div>
                ))}
            </div>
        </Card>
    </>
)

// ─────────────────────────────────────────────────────────────────────────────

export const NewPromotionRuleItem = {
    id: undefined,
    use_search_terms: false,
    promotion_broad: false,
    promotion_phrase: false,
    promotion_exact: false,
    promotion_products: false,
    negation_phrase: false,
    negation_exact: false,
    negation_products: false,
}

const isSameAdGroup = (
    a: { campaign_id: number; ad_group_id: number },
    b: { campaign_id: number; ad_group_id: number },
) => a.campaign_id == b.campaign_id && a.ad_group_id == b.ad_group_id

const ruleItemIsUsed = (ruleItem): boolean =>
    !!(
        ruleItem.use_search_terms ||
        ruleItem.promotion_broad ||
        ruleItem.promotion_phrase ||
        ruleItem.promotion_exact ||
        ruleItem.promotion_products ||
        ruleItem.negation_phrase ||
        ruleItem.negation_exact ||
        ruleItem.negation_products
    )

interface PromotionDetailDummyProps {
    screenshotMode: boolean

    items: PromotionItemI[]
    rules: any[] // @todo type
    setItems: (items: SetItems) => any
    setRules: any // @todo type

    disableAddItems?: boolean
    disableRemoveItems?: boolean
    /** Render the enhanced new-editor items table (badges, usage counts, campaign collapse) */
    useNewItemsTable?: boolean
    loading?: boolean
}

export const PromotionDetailDummy = ({
    screenshotMode,
    items,
    rules,
    setItems,
    setRules,
    disableAddItems,
    disableRemoveItems,
    useNewItemsTable = false,
    loading = false,
}: PromotionDetailDummyProps) => {
    const setRule = useSetRecoilState(activeRuleAtom)
    const [showAdd, setShowAdd] = useState<boolean>(false)
    const setActiveRule = useSetRecoilState(activeRuleAtom)

    const selectAllRules = (v) => {
        setRules((p) => {
            return p.map((i) => {
                i.enabled = v
                return i
            })
        })
    }

    const removeItem = (item) => {
        const rulesUsingItem = rules.filter((rule) =>
            (rule.rule_items ?? []).some(
                (ruleItem) => isSameAdGroup(ruleItem, item) && ruleItemIsUsed(ruleItem),
            ),
        )

        if (rulesUsingItem.length > 0) {
            const confirmed = window.confirm(
                `This ad group is used in ${rulesUsingItem.length} rule${rulesUsingItem.length === 1 ? '' : 's'}. ` +
                    'Removing it will also remove it from those rules. Are you sure you want to remove it?',
            )
            if (!confirmed) return
        }

        setItems((items: PromotionItemI[]): PromotionItemI[] =>
            items.filter((i) => {
                return !isSameAdGroup(i, item)
            }),
        )
        setRules((rules: PromotionRuleI[]): PromotionRuleI[] =>
            rules.map((rule) => ({
                ...rule,
                rule_items: (rule.rule_items ?? []).filter(
                    (ruleItem) => !isSameAdGroup(ruleItem, item),
                ),
            })),
        )
    }

    const allRulesSelected = useMemo(() => {
        return rules.map((i) => i.enabled).reduce((p, curr) => p & curr, true)
    }, [rules])

    const handleAddRule = (row) => {
        const itemsCopy = [...items]
        row.rule_items.forEach((item) => {
            itemsCopy.forEach((entry, index) => {
                if (
                    item.campaign_id == entry.campaign_id &&
                    item.ad_group_id == entry.ad_group_id
                ) {
                    itemsCopy.splice(index, 1)
                }
            })
        })

        itemsCopy.forEach((item, i) => {
            itemsCopy[i] = {
                ...item,
                ...NewPromotionRuleItem,
            }
        })

        const rule_items = groupItems([...row.rule_items, ...itemsCopy])

        setActiveRule({
            ...row,
            rule_items: [...rule_items],
        })
        setRule({
            ...row,
            rule_items: [...rule_items],
        })
    }

    const addRule = () => {
        const uuidValue = uuid()

        setRule({
            ...NewPromotionValues,
            // @ts-ignore
            rule_items: items.map((i) => ({
                ...i,
                ...NewPromotionRuleItem,
            })),
            uuid: uuidValue,
        })
    }

    const removeRule = (rule) => {
        setRules((rules) =>
            rules.filter((r) => {
                return !(rule.id ? r.id == rule.id : r.uuid == rule.uuid)
            }),
        )
    }

    if (loading) {
        return <PromotionDetailSkeleton />
    }

    if (useNewItemsTable) {
        return (
            <>
                <div>
                    <section className="pb-5">
                        {items.length === 0 ? (
                            <NewEmptyAdGroups
                                onAdd={() => setShowAdd(true)}
                                disabled={disableAddItems}
                            />
                        ) : (
                            <PromotionLinkedAdGroupsNew
                                items={items}
                                rules={rules}
                                setItems={setItems}
                                setShowAdd={setShowAdd}
                                screenshotMode={screenshotMode}
                                disableAddItems={disableAddItems}
                                disableRemoveItems={disableRemoveItems}
                                removeItem={removeItem}
                            />
                        )}
                    </section>

                    {items.length !== 0 && (
                        <section className="pt-5">
                            {rules.length === 0 ? (
                                <NewEmptyRules onAdd={addRule} />
                            ) : (
                                <PromotionRulesNew
                                    rules={rules}
                                    screenshotMode={screenshotMode}
                                    setRules={setRules}
                                    addRule={addRule}
                                    removeRule={removeRule}
                                    handleAddRule={handleAddRule}
                                />
                            )}
                        </section>
                    )}
                </div>

                <PromotionItemAddDialog
                    showAdd={showAdd}
                    setShowAdd={setShowAdd}
                    items={items}
                    setItems={setItems}
                    onUnlink={removeItem}
                />
            </>
        )
    }

    return (
        <>
            {/* Items Table*/}
            <Card className="d-flex flex-column p-0 mb-4 bp4-card-rounded w-full max-w-3xl mx-auto">
                {items.length === 0 && (
                    <EmptyState
                        title="No items"
                        icon={<Icon icon="search" size={60} />}
                        className="mb-4 py-12"
                    >
                        <div>Add the ad groups you wish to use in this promotion strategy</div>
                        {!disableAddItems && (
                            <Button
                                large
                                text="Link Ad Groups"
                                intent="primary"
                                icon="plus"
                                onClick={() => setShowAdd(true)}
                            />
                        )}
                    </EmptyState>
                )}
                {items.length > 0 && (
                    <PromotionItemsTableDummy
                        setShowAdd={setShowAdd}
                        screenshotMode={screenshotMode}
                        removeItem={removeItem}
                        items={items}
                        disableAddItems={disableAddItems}
                        disableRemoveItems={disableRemoveItems}
                    />
                )}
            </Card>

            {/* Rules Table*/}
            {items.length !== 0 && (
                <Card className="d-flex flex-column p-0 mb-4 bp4-card-rounded">
                    {rules.length === 0 && (
                        <EmptyState
                            title="No rules"
                            icon={<Icon icon="search" size={60} />}
                            className="mb-4 py-12"
                        >
                            <div>Add a new rule to this promotion strategy</div>
                            <Button
                                large
                                text="Add rule"
                                intent="primary"
                                icon="plus"
                                onClick={addRule}
                            />
                        </EmptyState>
                    )}
                    {rules.length > 0 && (
                        <PromotionRulesTable
                            addRule={addRule}
                            allRulesSelected={allRulesSelected}
                            selectAllRules={selectAllRules}
                            setRules={setRules}
                            screenshotMode={screenshotMode}
                            handleAddRule={handleAddRule}
                            removeRule={removeRule}
                            rules={rules}
                        />
                    )}
                </Card>
            )}

            <PromotionItemAddDialog
                showAdd={showAdd}
                setShowAdd={setShowAdd}
                items={items}
                setItems={setItems}
                onUnlink={removeItem}
            />
        </>
    )
}
